// The board's own picture library: high-definition pictures, free to use, that
// ship with the app and are served from its own address, so a theme can wear a
// real landscape without the board downloading anything or anyone else seeing
// who looks. The built-in themes wear them, and any agent can pick one by name
// (library:great-wave) when it has no web search to find its own. Each keeps
// its credit and license, shown where the picture is credited.

import type { ThemeBackground, ThemeColors } from "./types";
import { PICTURES } from "./libraryPictures";

export interface LibraryPicture {
  id: string;
  title: string;
  /** who made it, where it is kept, and its license, as Wikimedia Commons credits it */
  credit: string;
  license: "CC0" | "Public domain";
  /** why it is free to use, for the record */
  basis: string;
  /** its Wikimedia Commons file page, for the credit's link */
  page: string;
  /** the kept file's size */
  w: number;
  h: number;
  /** whether it reads as a dark or a light picture, measured */
  tone: "dark" | "light";
  /** its color where tiles do not cover it (the top and the sides), measured */
  edge: string;
  /** a whole theme's colors from it, measured the way the board measures any picture */
  colors: ThemeColors;
  mood: string[];
  place: string;
}

export const LIBRARY: LibraryPicture[] = PICTURES;
/** Every picture by its id. A map, so a name every object answers to (constructor, __proto__, toString) finds nothing. */
const BY_ID: ReadonlyMap<string, LibraryPicture> = new Map(LIBRARY.map((p) => [p.id, p]));

/** A library picture's key: lib/<id>.webp. */
export const LIB_KEY = /^lib\/([a-z0-9-]{2,40})\.webp$/;
export const libKey = (id: string) => `lib/${id}.webp`;

/** The library picture a key names, or null. */
export function libraryOf(key: string): LibraryPicture | null {
  const m = LIB_KEY.exec(key);
  return m ? (BY_ID.get(m[1]) ?? null) : null;
}

/** A library picture as a theme's background. */
export function libraryBackground(id: string): ThemeBackground | null {
  const p = BY_ID.get(id);
  return p ? { key: libKey(id), w: p.w, h: p.h, tone: p.edge, from: p.page } : null;
}

/** "library:great-wave", or the key itself: the picture it names, or null. */
export function libraryRef(raw: string): LibraryPicture | null {
  const t = raw.trim();
  const m = /^library:([a-z0-9-]{2,40})$/i.exec(t);
  if (m) return BY_ID.get(m[1].toLowerCase()) ?? null;
  return libraryOf(t);
}
